// Company stores (S3, 2026-10-12) — /store/admin/settings (store admin).
// Which orders need the admin's OK: none, every order, or orders over $X.
// PATCH /portal/store/settings; the API checks the rule and says why if not.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import { apiMessage, MY_STORE_KEY, updateStoreSettings, type ApprovalRule, type MyStore } from "@/lib/portal-store";

export function StoreSettingsPage() {
  return (
    <PortalLayout>
      <StoreGate admin>
        {(my) => (
          <>
            <StoreHeading store={my.store} title="Store settings" />
            <ApprovalRuleCard my={my} />
          </>
        )}
      </StoreGate>
    </PortalLayout>
  );
}

const RULES: Array<{ value: ApprovalRule; label: string }> = [
  { value: "none", label: "No approval needed" },
  { value: "all", label: "Approve every order" },
  { value: "over_amount", label: "Approve orders over" },
];

function ApprovalRuleCard({ my }: { my: MyStore }) {
  const qc = useQueryClient();
  const [rule, setRule] = useState<ApprovalRule>(my.store.approvalRule);
  const [amount, setAmount] = useState<string>(
    my.store.approvalThreshold != null ? String(my.store.approvalThreshold) : "",
  );
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      if (rule !== "over_amount") return updateStoreSettings({ approvalRule: rule });
      return updateStoreSettings({ approvalRule: rule, approvalThreshold: Number(amount) });
    },
    onSuccess: (next) => {
      qc.setQueryData(MY_STORE_KEY, next);
      setSaved(true);
    },
  });

  function onSave() {
    setSaved(false);
    setProblem(null);
    if (rule === "over_amount") {
      const n = Number(amount);
      if (amount.trim() === "" || !Number.isFinite(n) || n < 0) {
        setProblem("Enter the amount above which orders need your approval.");
        return;
      }
    }
    save.mutate();
  }

  return (
    <section className="rounded-lg border border-rule bg-white p-8 shadow-sm">
      <h2 className="mb-1 font-display text-xl font-bold tracking-tight">Order approval</h2>
      <p className="mb-6 text-sm text-brown-400">
        Choose which orders wait for your OK before we make them. Your own orders never need approval.
      </p>
      <fieldset className="max-w-md space-y-3">
        <legend className="sr-only">Order approval</legend>
        {RULES.map((r) => (
          <label key={r.value} className="flex min-h-[40px] cursor-pointer items-center gap-3 rounded-md border border-rule px-4 py-2 text-sm text-ink hover:bg-canvas">
            <input
              type="radio"
              name="approval-rule"
              value={r.value}
              checked={rule === r.value}
              onChange={() => {
                setRule(r.value);
                setSaved(false);
              }}
              className="accent-ink"
            />
            <span>{r.label}</span>
            {r.value === "over_amount" && (
              <span className="flex items-center gap-1">
                <span aria-hidden>$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  aria-label="Amount"
                  value={amount}
                  onFocus={() => setRule("over_amount")}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSaved(false);
                  }}
                  className="w-28 rounded-md border border-cream-300 bg-white px-2 py-1 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                />
              </span>
            )}
          </label>
        ))}
      </fieldset>
      {(problem || save.isError) && (
        <p role="alert" className="mt-4 text-sm text-rust-500">
          {problem ?? apiMessage(save.error)}
        </p>
      )}
      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          disabled={save.isPending}
          onClick={onSave}
          className="rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        {saved && <span className="text-sm text-brown-400">Saved.</span>}
      </div>
    </section>
  );
}
